import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../convex_generated/api'
import { useToast } from '../contexts/ToastContext'
import {
  BookOpenIcon,
  CodeBracketIcon,
  PlusIcon,
  EyeIcon,
  LockClosedIcon,
  CurrencyDollarIcon,
  StarIcon,
  TrashIcon,
  EyeSlashIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline'
import { asId } from '../lib/ids'

type TabType = 'all' | 'books' | 'forks'

const visibilityIcon = (visibility: string) => {
  if (visibility === 'private') return <LockClosedIcon className="w-4 h-4" title="Private" />
  if (visibility === 'paid') return <CurrencyDollarIcon className="w-4 h-4" title="Paid" />
  return <EyeIcon className="w-4 h-4" title="Public" />
}

const visibilityLabel: Record<string, string> = {
  public: 'Public',
  private: 'Private',
  paid: 'Paid',
}

// ── Per-card action menu ──────────────────────────────────────────────────

interface CardMenuProps {
  visibility: string
  onUnpublish: () => Promise<void>
  onDelete: () => void
}

const CardMenu: React.FC<CardMenuProps> = ({ visibility, onUnpublish, onDelete }) => {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  const isPublished = visibility !== 'private'

  const handleUnpublish = async () => {
    setBusy(true)
    setOpen(false)
    try { await onUnpublish() }
    finally { setBusy(false) }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="More options"
      >
        <EllipsisVerticalIcon className="w-5 h-5" />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-20 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-1 text-sm">
            {isPublished && (
              <button
                onClick={handleUnpublish}
                className="w-full flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <EyeSlashIcon className="w-4 h-4 text-amber-500" />
                Unpublish
              </button>
            )}
            <button
              onClick={() => { setOpen(false); onDelete() }}
              className="w-full flex items-center gap-2 px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ── Delete confirm dialog ─────────────────────────────────────────────────

interface DeleteDialogProps {
  title: string
  onConfirm: () => Promise<void>
  onCancel: () => void
}

const DeleteDialog: React.FC<DeleteDialogProps> = ({ title, onConfirm, onCancel }) => {
  const [busy, setBusy] = useState(false)

  const handleConfirm = async () => {
    setBusy(true)
    try { await onConfirm() }
    finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <TrashIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Delete branch?</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-300">"{title}"</span> will be permanently deleted. This cannot be undone.
            </p>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} disabled={busy} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={busy} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-60">
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

const LibraryPage: React.FC = () => {
  const { showToast } = useToast()
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null)

  const branches = useQuery(api.branches.getMyBranchesWithBooks)
  const deleteBranch = useMutation(api.branches.deleteBranch)
  const unpublishBranch = useMutation(api.payments.unpublishBranch)

  const isLoading = branches === undefined

  const ownBranches = (branches ?? []).filter((b) => !b.parentBranchId)
  const childBranches = (branches ?? []).filter((b) => !!b.parentBranchId)
  const displayed =
    activeTab === 'all' ? (branches ?? []) : activeTab === 'books' ? ownBranches : childBranches

  const tabs: { id: TabType; label: string; count: number }[] = [
    { id: 'all', label: 'All', count: (branches ?? []).length },
    { id: 'books', label: 'My Stories', count: ownBranches.length },
    { id: 'forks', label: 'My Branches', count: childBranches.length },
  ]

  const handleUnpublish = async (branchId: string, title: string) => {
    await unpublishBranch({ branchId: asId<"branches">(branchId) })
    showToast({ message: `"${title}" moved back to drafts`, type: 'success' })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    try {
      await deleteBranch({ branchId: asId<"branches">(deleteTarget.id) })
      showToast({ message: `"${deleteTarget.title}" deleted`, type: 'success' })
    } catch (e) {
      showToast({ message: e instanceof Error ? e.message : 'Delete failed', type: 'error' })
    } finally {
      setDeleteTarget(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {deleteTarget && (
        <DeleteDialog
          title={deleteTarget.title}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Library</h1>
            <p className="mt-1 text-gray-500 dark:text-gray-400">Your stories and branches</p>
          </div>
          <Link
            to="/editor"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200"
          >
            <PlusIcon className="w-5 h-5" />
            New Story
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-gray-200 dark:border-gray-700">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors duration-200 -mb-px ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-6 animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-6" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : displayed.length === 0 ? (
          <div className="text-center py-24">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-6">
              {activeTab === 'forks' ? (
                <CodeBracketIcon className="w-8 h-8 text-gray-400" />
              ) : (
                <BookOpenIcon className="w-8 h-8 text-gray-400" />
              )}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {activeTab === 'forks' ? 'No branches yet' : 'No stories yet'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {activeTab === 'forks'
                ? 'Browse the storefront and branch off a story to create an alternative path.'
                : 'Start writing your first story or explore the storefront to branch one.'}
            </p>
            <div className="flex items-center justify-center gap-3">
              <Link
                to="/editor"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                New Story
              </Link>
              <Link
                to="/storefront"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium rounded-lg transition-colors"
              >
                <BookOpenIcon className="w-4 h-4" />
                Explore Stories
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayed.map((branch) => (
              <div
                key={branch._id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden flex flex-col"
              >
                <div className="h-1 bg-gradient-to-r from-primary-500 to-primary-700" />

                <div className="p-6 flex flex-col flex-1">
                  {/* Header row: badges + menu */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center flex-wrap gap-2">
                      {branch.parentBranchId ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                          <CodeBracketIcon className="w-3 h-3" />
                          Branch
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                          <BookOpenIcon className="w-3 h-3" />
                          Story
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                        {visibilityIcon(branch.visibility)}
                        {visibilityLabel[branch.visibility] ?? branch.visibility}
                      </span>
                    </div>
                    <CardMenu
                      visibility={branch.visibility}
                      onUnpublish={() => handleUnpublish(branch._id, branch.title)}
                      onDelete={() => setDeleteTarget({ id: branch._id, title: branch.title })}
                    />
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 leading-snug">
                    {branch.title}
                  </h3>

                  {/* Book context */}
                  {branch.book && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      in{' '}
                      <Link
                        to={`/branches/${branch.book._id}`}
                        className="text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {branch.book.title}
                      </Link>
                    </p>
                  )}

                  {/* Stats */}
                  <div className="mt-auto flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 pt-4 border-t border-gray-100 dark:border-gray-700">
                    <span>{branch.wordCount?.toLocaleString() ?? 0} words</span>
                    {branch.ratingCount > 0 && (
                      <span className="flex items-center gap-1">
                        <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        {branch.rating.toFixed(1)} ({branch.ratingCount})
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-4">
                    <Link
                      to={`/reading/${branch._id}`}
                      className="flex-1 text-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Read
                    </Link>
                    <Link
                      to={`/editor/${branch._id}`}
                      className="flex-1 text-center px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                    >
                      Edit
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default LibraryPage

